import { finleyAssertUnreachable } from '@finley-technologies/common';
import { DbTable } from '@finley-technologies/finley-types';
import {
  BbplGraph,
  BbplGraphGenericGraph,
  progress,
  xlsx,
} from '@finley-technologies/tape-builder';

import {
  PickedCsvTape,
  PickedXlsxTape,
} from '../../../manualDataTapeUploads/ui/DataTapeFormatModal';
import {
  DataPipelineNodeData,
  Edge,
} from 'packages/dataPipeline/types/dataPipelineTypes';

export const getFieldInfo = ({
  graph,
  artifactId,
}: {
  graph: BbplGraphGenericGraph<
    xlsx.PartialArtifact & { type: DbTable.ArtifactType }
  >;
  artifactId: string;
}): {
  /** For data dictionary. */
  csvTape: PickedCsvTape | null;

  /** For data dictionary. */
  xlsxTape: PickedXlsxTape | null;
} => {
  const rawArtifacts = BbplGraph.findDependenciesRecursively(graph, [
    artifactId,
  ]).filter(
    ({ type }) =>
      type === 'rawLoanTape' ||
      type === 'rawSupplementalTape' ||
      type === 'rawRepaymentTape' ||
      type === 'rawCashBalanceFeed',
  );

  const dataDictionaryFieldGroups: xlsx.XlsxExtractionFieldGroup[] = [];
  rawArtifacts.forEach((rawArtifact) => {
    const fieldGroups = [
      ...xlsx.XlsxExtraction.getXlsxFieldGroups(graph, rawArtifact.id),
      ...xlsx.XlsxExtraction.getCsvFieldGroups(graph, rawArtifact.id),
    ];
    // Data dictionary is always associated with the raw node, because that's where the
    // upload button is shown.
    if (rawArtifact.id === artifactId) {
      dataDictionaryFieldGroups.push(...fieldGroups);
    }
  });

  let csvTape: PickedCsvTape | null = null;
  let xlsxTape: PickedXlsxTape | null = null;
  dataDictionaryFieldGroups.forEach((fieldGroup) => {
    switch (fieldGroup.mode) {
      case 'csv':
        csvTape = { fileType: 'csv', columns: fieldGroup.columns };
        break;
      case 'extractTape':
      case 'extractValues':
        if (!xlsxTape) {
          xlsxTape = {
            fileType: 'xlsx',
            valueFieldGroups: [],
            tapeFieldGroups: [],
          };
        }
        if (fieldGroup.mode === 'extractValues') {
          xlsxTape.valueFieldGroups.push(fieldGroup);
        } else {
          xlsxTape.tapeFieldGroups.push(fieldGroup);
        }
        break;
      default:
        finleyAssertUnreachable(fieldGroup);
    }
  });

  return { csvTape, xlsxTape };
};

/** Gets derived details to show in the bbpl monitor sidebar drawer. */
export const getDrawerDetails = ({
  nodes,
  edges,
  artifactId,
}: {
  nodes: DataPipelineNodeData[];
  edges: Edge[];
  artifactId: string;
}): {
  /** Errors associated with this graph node. */
  errors: progress.AggregatedError[];

  /** For data dictionary. */
  csvTape: PickedCsvTape | null;

  /** For data dictionary. */
  xlsxTape: PickedXlsxTape | null;
} => {
  const node = nodes.find((n) => n.artifact.id === artifactId);
  const artifact = node?.artifact;
  const instance = node?.instance;

  if (!artifact) {
    return { errors: [], csvTape: null, xlsxTape: null };
  }

  const { csvTape, xlsxTape } = getFieldInfo({
    // Reconstruct a graph structure.
    graph: {
      artifacts: nodes.map((n) => ({
        id: n.artifact.id,
        type: n.artifact.type,
        configuration: n.artifact.configuration,
        artifactSlug: n.artifact.externalDisplayName, // Workaround for now.
      })),
      dependencies: edges.map(({ source, target }) => ({
        inputArtifactId: source,
        outputArtifactId: target,
      })),
    },
    artifactId,
  });

  if (instance?.status !== 'error') {
    return { errors: [], csvTape, xlsxTape };
  }

  const errors: progress.AggregatedError[] = [];
  instance.statusDetails.errors.forEach((error) => {
    switch (error.type) {
      case 'structuredPublicError':
        errors.push({ ...error.content, type: 'structuredPublicError' });
        break;
      default:
        finleyAssertUnreachable(error.type);
    }
  });

  return { errors, csvTape, xlsxTape };
};
